import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Label } from "@/components/ui/Input";
import { ApiError } from "@/lib/api";
import { EM_DASH } from "@/lib/utils";
import {
  QC_INCIDENTS_QUERY_KEY,
  fetchQcIncidentIssueTiers,
  saveQcIncidentIssueTiers,
  type QcIncident,
} from "@/pages/manufacturing/qcIncidentsApi";

const ISSUE_TIERS_QUERY_KEY = ["qc-incident-issue-tiers"] as const;

function formatStamp(person: string | null, at: string | null): string | null {
  if (person == null && at == null) {
    return null;
  }
  const when = at == null ? null : new Date(at);
  const whenLabel =
    when != null && !Number.isNaN(when.getTime())
      ? when.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
      : at;
  if (person != null && whenLabel != null) {
    return `Last set by ${person} on ${whenLabel}`;
  }
  if (person != null) {
    return `Last set by ${person}`;
  }
  return whenLabel == null ? null : `Last set ${whenLabel}`;
}

function TierSelect({
  id,
  label,
  value,
  options,
  disabled,
  onChange,
  stamp,
}: {
  id: string;
  label: string;
  value: string;
  options: string[];
  disabled: boolean;
  onChange: (value: string) => void;
  stamp: string | null;
}): JSX.Element {
  const known = value === "" || options.includes(value);
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100 disabled:text-slate-500"
      >
        <option value="">Not selected</option>
        {!known ? <option value={value}>{value}</option> : null}
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {stamp != null ? <p className="mt-1 text-xs text-slate-500">{stamp}</p> : null}
    </div>
  );
}

export function QcIncidentIssueTiersFields({
  incident,
  writesAllowed,
  showTier1Editor,
  showTier2Editor,
}: {
  incident: QcIncident;
  writesAllowed: boolean;
  showTier1Editor: boolean;
  showTier2Editor: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const optionsQuery = useQuery({
    queryKey: ISSUE_TIERS_QUERY_KEY,
    queryFn: fetchQcIncidentIssueTiers,
  });
  const [tier1, setTier1] = useState(incident.tier1Issue ?? "");
  const [tier2, setTier2] = useState(incident.tier2Issue ?? "");

  useEffect(() => {
    setTier1(incident.tier1Issue ?? "");
    setTier2(incident.tier2Issue ?? "");
  }, [incident.id, incident.tier1Issue, incident.tier2Issue]);

  const tier1Dirty = showTier1Editor && tier1 !== (incident.tier1Issue ?? "");
  const tier2Dirty = showTier2Editor && tier2 !== (incident.tier2Issue ?? "");
  const dirty = tier1Dirty || tier2Dirty;

  const saveMutation = useMutation({
    mutationFn: () =>
      saveQcIncidentIssueTiers(incident.id, {
        ...(showTier1Editor ? { tier1_issue: tier1.trim() === "" ? null : tier1 } : {}),
        ...(showTier2Editor ? { tier2_issue: tier2.trim() === "" ? null : tier2 } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QC_INCIDENTS_QUERY_KEY });
    },
  });

  const error =
    saveMutation.error instanceof ApiError
      ? saveMutation.error.message
      : saveMutation.error instanceof Error
        ? saveMutation.error.message
        : optionsQuery.error instanceof Error
          ? optionsQuery.error.message
          : null;

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-2">
        {showTier1Editor ? (
          <TierSelect
            id={`qc-incident-${incident.id}-tier1`}
            label="Tier 1 issue"
            value={tier1}
            options={optionsQuery.data?.tier1 ?? []}
            disabled={!writesAllowed || saveMutation.isPending}
            onChange={setTier1}
            stamp={formatStamp(incident.tier1IssuePerson, incident.tier1IssueAt)}
          />
        ) : (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Tier 1 issue</p>
            <p className="mt-1 text-sm text-slate-900">{incident.tier1Issue ?? EM_DASH}</p>
            {formatStamp(incident.tier1IssuePerson, incident.tier1IssueAt) != null ? (
              <p className="mt-1 text-xs text-slate-500">
                {formatStamp(incident.tier1IssuePerson, incident.tier1IssueAt)}
              </p>
            ) : null}
          </div>
        )}
        {showTier2Editor ? (
          <TierSelect
            id={`qc-incident-${incident.id}-tier2`}
            label="Tier 2 issue"
            value={tier2}
            options={optionsQuery.data?.tier2 ?? []}
            disabled={!writesAllowed || saveMutation.isPending}
            onChange={setTier2}
            stamp={formatStamp(incident.tier2IssuePerson, incident.tier2IssueAt)}
          />
        ) : null}
      </div>
      {error != null ? <p className="text-sm text-rose-700">{error}</p> : null}
      {writesAllowed && (showTier1Editor || showTier2Editor) ? (
        <div className="flex justify-end">
          <HubSaveButton
            dirty={dirty}
            isLoading={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            Save issue tiers
          </HubSaveButton>
        </div>
      ) : null}
    </div>
  );
}
